import {
  FilterAltLight as FilterIcon,
  SubttasksLight as SubstacksIcon,
  VerticalCustom,
  WidgetAddLight as WidgetAddIcon,
} from '@repo/go-icons';
import { Button, Tooltip, Hint, HINT_DEFAULT_STYLE_OPTIONS } from '@repo/go-ui';
import { clsx } from 'clsx';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { WELL_LOGS_MANAGER_AVAILABLE } from 'src/api/consts';
import type { TabEntity } from 'src/entities/tab/TabEntity';
import { useService } from 'src/packages/di';

import { WellListPanelAsPopup } from '../well-list-panel-as-popup/WellListPanelAsPopup';
import type { WorkspaceHintsStore } from '../workspace/services/WorkspaceHintsStore';
import { HintNames } from '../workspace/services/WorkspaceHintsStore';
import type { WorkspaceStore } from '../workspace/Workspace.store';

import styles from './Toolbar.module.scss';

type Props = {
  workspaceStore: WorkspaceStore;
  workspaceHintsStore: WorkspaceHintsStore;
  tabsEntities: TabEntity[];
  actualTab: TabEntity | null;
  onSetActiveKey(key?: string): void;
  onCreateTab(): void;
  onRemoveTab(id: string): void;
  onTabClose?(id: string): void;
  onOpenInNewWindow?(tabId: string): void;
  onFullscreen: VoidFunction;
  onChangeTabName(id: string, name: string): void;
  onSaveTabAsTemplateModalOpen(): void;
  onTabSavedAsTemplateChange(tab: TabEntity | null): void;
  onUpdateTemplate(tab: TabEntity): void;
  disabledTabs: string[];
  activeKey?: string;
  onTabFocus?(id: string): void;
  onCreateWellListWidget?(): void;
  onCreateWellsFilterWidget?(): void;
  onCreateWellLogsManagerWidget?(): void;
  onCreateContentWidget: VoidFunction;
};

export const Toolbar = observer(function Toolbar({
  workspaceStore,
  workspaceHintsStore,
  actualTab,
  onCreateWellListWidget,
  onCreateWellLogsManagerWidget,
  onCreateWellsFilterWidget,
  onCreateContentWidget,
}: Props) {
  const { t } = useTranslation();
  const [containerRef, setContainerRef] = useState<HTMLElement | null>(null);

  const { isWellListInFSModeOpen, isWellListBlockedInFSMode } = workspaceStore;

  const wellsListHint = workspaceHintsStore.hintsMap.get(HintNames.wellsList);
  const contentWidgetHint = workspaceHintsStore.hintsMap.get(HintNames.contentWidget);

  const actualTabGroup = actualTab?.groupManager.groups.find(
    (group) => group.id === actualTab.wellListPanelState?.groupId,
  );
  const directories = useService('directories');
  const wells = directories.getObject('GOdashboard_Well');
  const wellName =
    wells?.find((item) => item.id === actualTab?.wellListPanelStore.selectedWellId)?.data.name ??
    actualTab?.wellListPanelStore.selectedWellId;

  return (
    <div className={styles.toolbar} style={{ marginRight: '16px', columnGap: '0' }} ref={(ref) => setContainerRef(ref)}>
      {containerRef && (
        <div className={styles.widgetsToolbar}>
          <Tooltip.Multi
            tooltips={[
              {
                key: 'wellListTooltip',
                trigger: wellsListHint?.isVisible || isWellListInFSModeOpen ? [] : undefined,
                title: wellName ?? t('dashboard:Toolbar.openWellsList'),
                delay: 400,
                open: wellsListHint?.isVisible || isWellListInFSModeOpen ? false : undefined,
              },
              {
                key: 'blockedWellListHint',
                trigger: isWellListBlockedInFSMode ? undefined : [],
                title: t('hints:blockedWellListHint.title'),
                open: isWellListBlockedInFSMode ? undefined : false,
              },
              {
                key: 'wellListHint',
                title: (
                  <Hint.HintContent
                    title={t(wellsListHint?.data.title ?? '')}
                    description={t(wellsListHint?.data.description ?? '')}
                  />
                ),
                open: wellsListHint?.isVisible && !isWellListInFSModeOpen,
                rootContainer: containerRef,
                ...HINT_DEFAULT_STYLE_OPTIONS,
              },
              {
                key: 'wellListWidgetAsPopup',
                title: (
                  <WellListPanelAsPopup
                    actualTab={actualTab}
                    onClosePopup={() => workspaceStore.setIsWellListInFSModeOpen(false)}
                    onCreateNewTabAccept={(template, wellId, customTabName) => {
                      workspaceStore.createTab();
                      workspaceStore.onCreateNewTabAccept(template, wellId, customTabName);
                    }}
                  />
                ),
                open: isWellListInFSModeOpen,
                onOpenChange: (value) => {
                  if (!value && actualTab?.wellListPanelState?.isPinned) {
                    return;
                  }

                  workspaceStore.setIsWellListInFSModeOpen(value);
                },
                withArrow: false,
                trigger: ['click'],
                gap: 8,
                className: styles.wellListPopup,
                rootContainer: containerRef,
              },
            ]}
          >
            <div style={{ position: 'relative' }}>
              <Button
                variant={isWellListInFSModeOpen ? 'primary' : 'flat'}
                icon={<VerticalCustom className={styles.icon} />}
                onClick={() => onCreateWellListWidget?.()}
                className={clsx(styles.toolbarIcon)}
                style={{
                  backgroundColor: actualTabGroup?.color,
                  color: actualTabGroup?.contrastColor,
                }}
              />
            </div>
          </Tooltip.Multi>
          {WELL_LOGS_MANAGER_AVAILABLE && (
            <Tooltip title={t('widgets:WellLogsManager.title')}>
              <Button
                variant="flat"
                icon={<SubstacksIcon className={styles.icon} />}
                onClick={() => onCreateWellLogsManagerWidget?.()}
                className={clsx(styles.toolbarIcon)}
              />
            </Tooltip>
          )}
          <Tooltip title={t('dashboard:Toolbar.openFilters')} delay={400}>
            <Button
              variant="flat"
              icon={<FilterIcon className={styles.icon} />}
              onClick={() => onCreateWellsFilterWidget?.()}
              className={styles.toolbarIcon}
            />
          </Tooltip>
          <Tooltip.Multi
            tooltips={[
              {
                key: 'contentWidgetTooltip',
                trigger: contentWidgetHint?.isVisible ? [] : undefined,
                title: t('dashboard:Toolbar.addNewWidget'),
                delay: 400,
              },
              {
                key: 'contentWidgetHint',
                title: (
                  <Hint.HintContent
                    title={t(contentWidgetHint?.data.title ?? '')}
                    description={t(contentWidgetHint?.data.description ?? '')}
                  />
                ),
                placement: 'bottom-end',
                open: contentWidgetHint?.isVisible,
                ...HINT_DEFAULT_STYLE_OPTIONS,
                arrowOptions: {
                  ...HINT_DEFAULT_STYLE_OPTIONS.arrowOptions,
                },
                rootContainer: containerRef,
              },
            ]}
          >
            <Button
              variant="flat"
              icon={<WidgetAddIcon className={styles.icon} />}
              onClick={onCreateContentWidget}
              className={styles.toolbarIcon}
            />
          </Tooltip.Multi>
        </div>
      )}

      {/* <div className={styles.widgetsToolbar}>
        {EXPORT_CSV_AVAILABLE && <UserExportTasksButton />}
        <Tooltip title={t('dashboard:Toolbar.enableFullScreen')} delay={400}>
          <Button
            variant="flat"
            icon={<FullIcon className={styles.icon} />}
            title={t('dashboard:Toolbar.enableFullScreen')}
            onClick={onFullscreen}
            className={styles.toolbarIcon}
          />
        </Tooltip>
      </div> */}
    </div>
  );
});
